import { GetMinimumOrderQuantity, GetPriceForQuantity, GetPricingLine } from 'features/commerce/pricing/pricing-result';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import ProductPricing from 'shared/components/commerce/ProductPricing';
import Spinner from 'shared/components/Spinner';
import { dispatchRocEvent } from 'shared/hooks/useRocEventHandler';
import { useProductDetailsContext } from '../ProductDetailsContext';
import { ProductType } from '../types';
import ProductAddToCart from '../ProductAddToCart';
import { ProductTable } from './ProductTable';
import { ProductBreadcrumbs, ProductMedia } from '../ProductDetails';
import { MY_ACCOUNT_LOGIN_URL } from 'features/my-account/routes.roc';
import { Localizer } from 'services/Localizer';
import { useTapeCaseProductDetailsContext } from './TapeCaseProductDetailsContext';
import { TapeCaseProductDetailsProvider } from './TapeCaseProductDetailsContextProvider';
import { ProductDetailsProvider } from './ProductDetailsProvider';
import { ProductQuantityBreaks } from './ProductQuantityBreaks';

/**
 * Main, project-specific component that renders the product details using react portals
 */
function TapeCaseProductDetails() {
	const [canGoBack, setCanGoBack] = useState('');

	useEffect(() => {
		setCanGoBack(sessionStorage.getItem('canGoBack') || '');
		sessionStorage.removeItem('canGoBack');
	}, []);

	return (
		<ProductDetailsProvider>
			<TapeCaseProductDetailsProvider>
				<ProductBreadcrumbs canGoBack={canGoBack} />
				<ProductMedia />
				<ContractPricingLoginLink />
				<ProductDetails />
			</TapeCaseProductDetailsProvider>
		</ProductDetailsProvider>
	);
}

function ProductDetails() {
	const {
		state: { productDetails, groupId },
	} = useProductDetailsContext();

	useEffect(() => {
		dispatchRocEvent('product-viewed', {
			productName: productDetails.summary.name,
			sku: productDetails.summary.sku,
			productId: productDetails.id,
			productGroupId: groupId !== '' ? groupId : null,
			selections: productDetails.selections ?? null,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const additionalProductInfoLink = document.querySelector(
			`[data-testid="product-page-additional-product-info-link"]`,
		);
		if (additionalProductInfoLink == null || !additionalProductInfoLink.classList.contains('roc-hidden')) {
			// link element not found or it's already displayed - no need for additional manipulation on the client-side
			return;
		}

		// this is the case when Additional Product Info link is hidden
		// we need to check if there are any tabs loaded on the client side
		if (productDetails.tabs != null && productDetails.tabs.some((t) => t.model != null || t.rawHtml != null)) {
			additionalProductInfoLink.classList.remove('roc-hidden');
		}
	}, [productDetails.tabs]);

	return productDetails.summary.type == ProductType.Group ? (
		<>
			<ProductTable />
		</>
	) : (
		<>
			<ProductHeader />
			<AddToCart />
		</>
	);
}

//#region React Portals

/**
 * Product page main info header, containing the pricing (w/o rating like it is in framework)
 */
export function ProductHeader() {
	const {
		state: { productDetails, hasError, productQuantity },
		actor: { isLoadingAny },
	} = useProductDetailsContext();

	const mainInfoHeaderDom = document.getElementById('product-details-main-info-header');
	if (!mainInfoHeaderDom) {
		console.warn('Main info header DOM element not found.');
		return null;
	}

	const getDetail = () => {
		if (productDetails.displayPricing == null) {
			return null;
		}
		const selectedPricingLineId =
			productDetails.pricingLines &&
			productDetails.pricingLines.selected &&
			productDetails.pricingLines.selected.id;

		const displayPriceLine = GetPricingLine(
			productDetails.displayPricing,
			productDetails.id,
			selectedPricingLineId,
		);

		let moq = productQuantity;
		if (selectedPricingLineId && productDetails.displayPricing) {
			const selectedPricingLine = GetPricingLine(
				productDetails.displayPricing,
				productDetails.id,
				selectedPricingLineId,
			);
			if (selectedPricingLine) {
				moq = GetMinimumOrderQuantity(selectedPricingLine);
			}
		}

		return (
			displayPriceLine && GetPriceForQuantity(displayPriceLine.prices, Math.max(moq ?? 1, productQuantity ?? 1))
		);
	};

	const productHeaderInfo = (
		<>
			{!isLoadingAny(['displayPricing']) ? (
				<>
					{!productDetails.summary.isDiscontinued && (
						<>
							<ProductPricing
								displayPrice={getDetail()}
								className="roc-pdp-price"
								inline
								testId="pdp-main-price"
							/>
							<ProductQuantityBreaks />
						</>
					)}
				</>
			) : !hasError ? (
				<Spinner block />
			) : null}
		</>
	);

	return ReactDOM.createPortal(productHeaderInfo, mainInfoHeaderDom);
}

/**
 * Add to Cart and Quantity components
 */
export function AddToCart() {
	const {
		state: { productDetails, productQuantity },
		actor: { setProductQuantity },
	} = useProductDetailsContext();

	const productAddToCartDom = document.getElementById('product-add-to-cart');
	if (!productAddToCartDom) {
		return null;
	}

	const productAddToCart = (
		<ProductAddToCart
			id={productDetails.id}
			summary={productDetails.summary}
			availability={productDetails.availability}
			pricingLines={productDetails.pricingLines}
			quantity={productQuantity}
			displayPricing={productDetails.displayPricing}
			setQuantity={setProductQuantity}
			onPricingLineChange={() => {}}
			setSelectedProductPricingLineId={() => {}}
		/>
	);

	return ReactDOM.createPortal(productAddToCart, productAddToCartDom);
}

/**
 * Login Link for Contract Pricing
 */
export function ContractPricingLoginLink() {
	const {
		state: { productUrl },
		actor: { isContractPricingAvailable },
	} = useTapeCaseProductDetailsContext();

	const contractPricingLoginLinkDom = document.getElementById('product-contract-pricing-login-link');
	if (!contractPricingLoginLinkDom) {
		return null;
	}

	if (isContractPricingAvailable()) {
		contractPricingLoginLinkDom.classList.remove('roc-hidden');
	}

	const contractPricingLoginLink = (
		<>
			{isContractPricingAvailable() == true ? (
				<a href={MY_ACCOUNT_LOGIN_URL + '?ReturnUrl=' + encodeURIComponent(productUrl ?? '')}>
					{Localizer('LoginToSeeContractPricing')}
				</a>
			) : null}
		</>
	);

	return ReactDOM.createPortal(contractPricingLoginLink, contractPricingLoginLinkDom);
}

export default TapeCaseProductDetails;
